import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { leaveApi } from './leaveApi';
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX } from '../common/tableStyles';
import { formatDate } from '../../utils/dateFormat';

const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';

export default function ManagerDelegationPanel() {
  const [delegations, setDelegations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    assigned_manager_id: '',
    delegated_to_id: '',
    start_date: '',
    end_date: '',
    reason: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchDelegations = useCallback(async () => {
    setIsLoading(true);
    setErrorMsg('');
    try {
      const data = await leaveApi.getDelegations();
      setDelegations(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to load manager delegations.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDelegations();
  }, [fetchDelegations]);

  const handleOpenDialog = () => {
    setFormData({
      assigned_manager_id: '',
      delegated_to_id: '',
      start_date: '',
      end_date: '',
      reason: '',
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (!isSubmitting) setDialogOpen(false);
  };

  const handleSubmit = async () => {
    if (!formData.delegated_to_id || !formData.start_date || !formData.end_date) {
      setErrorMsg('Please provide the delegatee employee ID and valid date range.');
      return;
    }
    if (formData.assigned_manager_id && formData.assigned_manager_id === formData.delegated_to_id) {
      setErrorMsg('Self-delegation is prohibited: you cannot delegate approval authority to yourself.');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      await leaveApi.createDelegation(formData);
      setSuccessMsg('Delegation authority granted successfully.');
      setDialogOpen(false);
      fetchDelegations();
    } catch (err) {
      const resp = err.response?.data;
      const detail = resp?.detail || (Array.isArray(resp?.non_field_errors) ? resp.non_field_errors.join(', ') : null) || 'Failed to create delegation.';
      setErrorMsg(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Are you sure you want to revoke this delegation immediately?')) return;
    try {
      await leaveApi.revokeDelegation(id);
      setSuccessMsg('Delegation revoked successfully.');
      fetchDelegations();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to revoke delegation.');
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Header Card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '14px',
                bgcolor: `${ACCENT}14`,
                color: ACCENT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SupervisorAccountIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
                Manager Leave Approval Delegations
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Temporary transfer of leave approval authority during planned absence or leaves.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            sx={{
              bgcolor: ACCENT,
              fontWeight: 700,
              borderRadius: '12px',
              textTransform: 'none',
              '&:hover': { bgcolor: '#2557d6' },
            }}
          >
            Grant Delegation
          </Button>
        </Box>
      </Paper>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setErrorMsg('')}>
          {errorMsg}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {/* Delegations Table */}
      <TableContainer component={Paper} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', border: '1px solid #eef0f4' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD_CELL_SX}>ASSIGNED MANAGER</TableCell>
              <TableCell sx={HEAD_CELL_SX}>DELEGATED TO</TableCell>
              <TableCell sx={HEAD_CELL_SX}>EFFECTIVE WINDOW</TableCell>
              <TableCell sx={HEAD_CELL_SX}>STATUS</TableCell>
              <TableCell sx={HEAD_CELL_SX}>REASON</TableCell>
              <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'center' }}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : delegations.length > 0 ? (
              delegations.map((del) => (
                <TableRow key={del.id} sx={TABLE_ROW_SX}>
                  <TableCell sx={BODY_CELL_SX}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                      {del.assigned_manager_name || `Emp #${del.assigned_manager_id || del.assigned_manager}`}
                    </Typography>
                  </TableCell>
                  <TableCell sx={BODY_CELL_SX}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: ACCENT }}>
                      {del.delegated_to_name || `Emp #${del.delegated_to_id || del.delegated_to}`}
                    </Typography>
                  </TableCell>
                  <TableCell sx={BODY_CELL_SX}>
                    <Typography variant="body2" sx={{ color: '#374151' }}>
                      {formatDate(del.start_date)} → {formatDate(del.end_date)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={BODY_CELL_SX}>
                    <Chip
                      size="small"
                      label={del.is_active ? 'ACTIVE' : 'INACTIVE'}
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        borderRadius: '8px',
                        bgcolor: del.is_active ? '#ecfdf5' : '#f3f4f6',
                        color: del.is_active ? '#059669' : '#6b7280',
                        border: `1px solid ${del.is_active ? '#a7f3d0' : '#e5e7eb'}`,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={BODY_CELL_SX}>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      {del.reason || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...BODY_CELL_SX, textAlign: 'center' }}>
                    {del.is_active && (
                      <IconButton
                        size="small"
                        title="Revoke Delegation"
                        onClick={() => handleRevoke(del.id)}
                        sx={{ color: '#dc2626', bgcolor: '#fef2f2', '&:hover': { bgcolor: '#fee2e2' } }}
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#6b7280' }}>
                  No active or past delegations found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Grant Delegation Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Grant Approval Delegation</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Assigning Manager (Emp ID / PK)"
                placeholder="Leave blank if self"
                fullWidth
                size="small"
                value={formData.assigned_manager_id}
                onChange={(e) => setFormData({ ...formData, assigned_manager_id: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Delegatee Colleague (Emp ID / PK) *"
                placeholder="Target employee ID"
                required
                fullWidth
                size="small"
                value={formData.delegated_to_id}
                onChange={(e) => setFormData({ ...formData, delegated_to_id: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Effective From *"
                type="date"
                required
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Effective To *"
                type="date"
                required
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Business Justification"
                placeholder="e.g. Annual Leave cover"
                multiline
                rows={3}
                fullWidth
                size="small"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} disabled={isSubmitting} sx={{ borderRadius: '10px' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            sx={{ bgcolor: ACCENT, borderRadius: '10px', '&:hover': { bgcolor: '#2557d6' } }}
          >
            {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Confirm Delegation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
